import { Box } from '@mui/material';
import { useLayout } from '@orgnc/core/hooks';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import * as React from 'react';
import { Logo } from '@orgnc/core/components/atoms';
import SocialList from '@/components/header/SocialList';
import { useEffect, useRef, useState } from 'react';
import GoogleAdTag from '@/components/GoogleAdTag';
import NavBar from './NavBar';
import { useAimGeneralOptions } from '../../../utils/useAimGeneralOptions';
import HeaderCard, { HeaderCardProps } from '../HeaderCard';

const CARD_STYLES: HeaderCardProps['sx'] = {
  title: {
    color: 'text.secondary',
    fontSize: {
      sm: '8px',
      lg: '10px',
    },
    fontWeight: '700',
  },
  subtitle: {
    color: 'text.secondary',
    fontSize: {
      sm: '12px',
      lg: '13px',
    },
    fontWeight: '700',
  },
};

const Header = () => {
  const { menus, networks, logos, newsletter } = useLayout();
  const appBarRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const { headerCardsByColumn } = useAimGeneralOptions();

  useEffect(() => {
    setHeaderHeight(appBarRef?.current?.offsetHeight || 0);
  }, [appBarRef]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="relative"
        sx={{
          backgroundColor: 'common.white',
          zIndex: ({ zIndex }) => zIndex.drawer - 1,
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            mt: 1,
            mx: 2,
            display: {
              xs: 'none',
              sm: 'flex',
            },
            a: {
              textDecoration: 'unset',
              color: 'secondary.main',
            },
          }}
        >
          <SocialList networks={networks} />
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            {menus.HEADER_TOP_NAVIGATION?.map(
              (item) =>
                item.path && (
                  <Typography
                    key={`${item.label}.${item.path}`}
                    component="a"
                    href={item.path}
                    target={item.target || ''}
                    rel={item.target ? 'noopener' : undefined}
                    sx={{
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'secondary.light',
                      },
                      fontSize: 15,
                    }}
                  >
                    {item.label}
                  </Typography>
                )
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: {
              xs: 'none',
              sm: 'flex',
            },
            maxWidth: 1100,
            mx: 'auto',
            px: 2,
            my: 2,
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {headerCardsByColumn && headerCardsByColumn.left.length > 0 && (
            <Box sx={{ display: 'flex', mr: { sm: 0, lg: 25 } }}>
              {headerCardsByColumn?.left.map((card, idx) => (
                <HeaderCard
                  key={`left-${idx}`}
                  headerCard={card}
                  sx={CARD_STYLES}
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              mr: {
                sm: 0,
                lg: 25,
              },
            }}
          >
            <Box sx={{ width: { sm: 200, md: 300, lg: 458 }, display: 'flex' }}>
              {logos.main?.sourceUrl && (
                <Logo
                  sourceUrl={logos.main.sourceUrl}
                  width={458}
                  height="100%"
                  title={logos.main.altText}
                  sx={{}}
                />
              )}
            </Box>
          </Box>
          {headerCardsByColumn && headerCardsByColumn.right.length > 0 && (
            <Box sx={{ display: 'flex' }}>
              {headerCardsByColumn?.right.map((card, idx) => (
                <HeaderCard
                  key={`right-${idx}`}
                  headerCard={card}
                  sx={CARD_STYLES}
                />
              ))}
            </Box>
          )}
        </Box>
        <NavBar
          menu={menus.MAIN_NAVIGATION}
          categoriesMenu={menus.HEADER_NAVIGATION}
          networks={networks}
          additionalMenu={menus.FOOTER_NAVIGATION}
          newsletter={newsletter}
          logo={logos.main || undefined}
          headerHeight={headerHeight}
        />
      </AppBar>
      <GoogleAdTag
        zone="header"
        sizes={[
          [300, 50],
          [300, 100],
          [728, 90],
          [970, 90],
          [970, 250],
        ]}
        mapping={[
          [
            [1024, 768],
            [
              [728, 90],
              [970, 90],
              [970, 250],
            ],
          ],
          [[768, 0], [[728, 90]]],
          [
            [0, 0],
            [
              [300, 50],
              [300, 100],
            ],
          ],
        ]}
        preserveSpace={false}
      />
    </>
  );
};

export default Header;
